import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Heart, ExternalLink, MapPin, Bed, Bath, Square } from "lucide-react";
import { Button } from "../components/ui/Button";
import { Navbar } from "../components/Navbar";
import { useAuth } from "../contexts/AuthContext";
import { addToFavorites, removeFromFavorites, isPropertyFavorite } from "../supabase";

const Properties = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [properties, setProperties] = useState([]);
  const [favorites, setFavorites] = useState(new Set());
  const [loading, setLoading] = useState(true);

  // Cargar favoritos del usuario
  const loadFavorites = useCallback(async (propertiesToCheck) => {
    if (!user || !propertiesToCheck || propertiesToCheck.length === 0) return;
    
    const favoritesSet = new Set();
    for (const property of propertiesToCheck) {
      const propertyCode = property.propertyCode || property.url;
      const isFav = await isPropertyFavorite(user.uid, propertyCode);
      if (isFav) {
        favoritesSet.add(propertyCode);
      }
    }
    setFavorites(favoritesSet);
  }, [user]);

  useEffect(() => {
    // Verificar si hay propiedades en el estado de navegación
    if (location.state?.properties) {
      const messageId = location.state.messageId;
      setProperties(location.state.properties);
      
      // Guardar en sessionStorage con el ID del mensaje como clave única
      if (messageId) {
        const storageKey = `chatProperties_${messageId}`;
        sessionStorage.setItem(storageKey, JSON.stringify({
          properties: location.state.properties,
          propertiesCount: location.state.propertiesCount || location.state.properties.length,
          messageId: messageId
        }));
      }
      
      setLoading(false);
    } else {
      // Si no hay state, intentar recuperar de sessionStorage
      // Buscar el messageId en los parámetros de la URL o buscar la última entrada
      const urlParams = new URLSearchParams(window.location.search);
      const messageId = urlParams.get('messageId');
      
      let savedProperties = null;
      
      if (messageId) {
        // Si hay messageId en la URL, usar esa clave específica
        const storageKey = `chatProperties_${messageId}`;
        savedProperties = sessionStorage.getItem(storageKey);
      } else {
        // Si no hay messageId, buscar todas las claves de propiedades y usar la más reciente
        const allKeys = Object.keys(sessionStorage);
        const propertyKeys = allKeys.filter(key => key.startsWith('chatProperties_'));
        
        if (propertyKeys.length > 0) {
          // Ordenar por timestamp si está disponible, o usar la última modificada
          let mostRecentKey = propertyKeys[0];
          let mostRecentTime = 0;
          
          propertyKeys.forEach(key => {
            try {
              // Buscar el timestamp más reciente (el ID del mensaje es un timestamp)
              const keyTimestamp = parseInt(key.replace('chatProperties_', ''));
              if (keyTimestamp > mostRecentTime) {
                mostRecentTime = keyTimestamp;
                mostRecentKey = key;
              }
            } catch (e) {
              // Ignorar claves inválidas
            }
          });
          
          savedProperties = sessionStorage.getItem(mostRecentKey);
        }
      }
      
      if (savedProperties) {
        try {
          const data = JSON.parse(savedProperties);
          setProperties(data.properties);
          setLoading(false);
        } catch (error) {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    }
  }, [location.state]);

  useEffect(() => {
    if (user && properties.length > 0) {
      loadFavorites(properties);
    }
  }, [user, properties, loadFavorites]);

  const toggleFavorite = async (property) => {
    if (!user) {
      alert('Debes iniciar sesión para guardar favoritos');
      return;
    }

    const propertyCode = property.propertyCode || property.url;
    const isCurrentlyFavorite = favorites.has(propertyCode);

    try {
      if (isCurrentlyFavorite) {
        await removeFromFavorites(user.uid, propertyCode);
        setFavorites(prev => {
          const newSet = new Set(prev);
          newSet.delete(propertyCode);
          return newSet;
        });
      } else {
        await addToFavorites(user.uid, property);
        setFavorites(prev => new Set([...prev, propertyCode]));
      }
    } catch (error) {
      alert('Error al actualizar favoritos');
    }
  };

  const formatPrice = (price) => {
    if (!price) return 'Precio no disponible';
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  const formatSize = (size) => {
    if (!size) return 'N/A';
    return `${size} m²`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Cargando propiedades...</p>
        </div>
      </div>
    );
  }

  if (properties.length === 0) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Navbar />
        <main className="flex-1 container mx-auto px-4 pt-20 pb-6">
          <div className="text-center py-20">
            <h1 className="text-2xl font-bold mb-4">No hay propiedades disponibles</h1>
            <p className="text-muted-foreground mb-6">No se encontraron propiedades para mostrar.</p>
            <Button onClick={() => navigate('/chat')} variant="hero">
              Volver al chat
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      
      <main className="flex-1 container mx-auto px-4 pt-20 pb-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/chat')}
              variant="ghost"
              size="icon"
              className="flex-shrink-0"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">Propiedades encontradas</h1>
              <p className="text-muted-foreground">{properties.length} propiedades disponibles</p>
            </div>
          </div>
        </div>

        {/* Properties Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {properties.map((property) => {
            const propertyCode = property.propertyCode || property.url;
            const propertyKey = propertyCode || property.id || `property-${Math.random()}`;
            const isFavorite = favorites.has(propertyCode);
            
            return (
              <div key={propertyKey} className="bg-card rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300">
                {/* Property Image */}
                <div className="relative h-48 bg-secondary">
                  {(() => {
                    // Buscar la imagen en diferentes campos posibles
                    const imageUrl = property.thumbnail || 
                                   property.image || 
                                   property.images?.[0] || 
                                   property.photo || 
                                   property.photos?.[0] ||
                                   property.mainImage ||
                                   property.picture ||
                                   property.media?.[0] ||
                                   property.mediaUrl ||
                                   property.imageUrl ||
                                   property.url ||
                                   property.visual?.[0] ||
                                   property.visuals?.[0] ||
                                   property.mediaFiles?.[0] ||
                                   property.attachments?.[0];
                    
                    if (imageUrl) {
                      // Crear URL con proxy para evitar CORS
                      const proxyUrl = `https://images.weserv.nl/?url=${encodeURIComponent(imageUrl)}`;
                      
                      return (
                        <img
                          src={proxyUrl}
                          alt={property.suggestedTexts?.title || property.title || 'Propiedad'}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            const placeholder = e.target.parentElement.querySelector('.image-placeholder');
                            if (placeholder) {
                              placeholder.style.display = 'flex';
                            }
                          }}
                          onLoad={(e) => {
                            const placeholder = e.target.parentElement.querySelector('.image-placeholder');
                            if (placeholder) {
                              placeholder.style.display = 'none';
                            }
                          }}
                        />
                      );
                    } else {
                      // Si no hay URL, mostrar placeholder
                      return (
                        <div className="w-full h-full flex items-center justify-center text-muted-foreground bg-gradient-to-br from-secondary to-secondary/50">
                          <div className="text-center">
                            <Square className="w-12 h-12 mx-auto mb-2" />
                            <p className="text-sm">Sin imagen</p>
                          </div>
                        </div>
                      );
                    }
                  })()}
                  
                  {/* Placeholder cuando no hay imagen o falla */}
                  <div className="image-placeholder w-full h-full flex items-center justify-center text-muted-foreground bg-gradient-to-br from-secondary to-secondary/50" style={{ display: 'none' }}>
                    <div className="text-center">
                      <Square className="w-12 h-12 mx-auto mb-2" />
                      <p className="text-sm">Sin imagen</p>
                    </div>
                  </div>
                  
                  {/* Favorite Button */}
                  <button
                    onClick={() => toggleFavorite(property)}
                    className="absolute top-3 right-3 p-2 bg-background/80 backdrop-blur-sm rounded-full hover:bg-background transition-colors"
                  >
                    <Heart 
                      className={`w-5 h-5 ${
                        isFavorite ? 'fill-red-500 text-red-500' : 'text-muted-foreground'
                      }`} 
                    />
                  </button>
                </div>

                {/* Property Info */}
                <div className="p-4">
                  <h3 className="font-semibold text-lg mb-2 line-clamp-2">
                    {property.suggestedTexts?.title || 'Propiedad sin título'}
                  </h3>
                  
                  <div className="flex items-center gap-1 text-muted-foreground mb-3">
                    <MapPin className="w-4 h-4" />
                    <span className="text-sm">{property.address || 'Ubicación no disponible'}</span>
                  </div>

                  {/* Property Details */}
                  <div className="flex items-center gap-4 mb-4 text-sm text-muted-foreground">
                    {property.rooms && (
                      <div className="flex items-center gap-1">
                        <Bed className="w-4 h-4" />
                        <span>{property.rooms} hab</span>
                      </div>
                    )}
                    {property.bathrooms && (
                      <div className="flex items-center gap-1">
                        <Bath className="w-4 h-4" />
                        <span>{property.bathrooms} baños</span>
                      </div>
                    )}
                    {property.size && (
                      <div className="flex items-center gap-1">
                        <Square className="w-4 h-4" />
                        <span>{formatSize(property.size)}</span>
                      </div>
                    )}
                  </div>

                  {/* Price */}
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="text-2xl font-bold text-primary">
                        {formatPrice(property.priceInfo?.price?.amount)}
                      </p>
                      {property.priceInfo?.price?.currency && (
                        <p className="text-sm text-muted-foreground">
                          {property.priceInfo.price.currency}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    <Button
                      onClick={() => window.open(property.url, '_blank')}
                      variant="hero"
                      className="flex-1"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Ver en Idealista
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
};

export default Properties;
